import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Swap } from '../ApiTypes';
import { getSwaps } from '../api';
import FaceFilter from '../components/FaceFilter';
import SwapItem, { SwapItemSkeleton } from '../components/SwapItem';
import { CenterContainer } from '../styles/Containers';
import { SearchInput } from '../styles/Inputs';
import { matchesSwapQuery } from '../swaps';
import { removeDuplicates, useAsyncEffect } from '../utils';

const SKELETON_COUNT = 8;

const PageContainer = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 30px 30px 100px 30px;

  @media (max-width: 560px) {
    padding: 20px 16px 60px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;

  @media (max-width: 560px) {
    font-size: 20px;
  }
`;

const SwapCount = styled.span`
  font-size: 14px;
  color: #8a8a8a;
`;

const SwapListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  max-width: 520px;
`;

const NoResult = styled.div`
  margin-top: 30px;
  color: #8a8a8a;
`;

const LetsCreate = styled.div`
  font-size: 20px;
  color: rgb(254, 167, 0);
  margin-top: 30px;
`;

const SwapListPage: FC<{  }> = (props) => {
  const { t } = useTranslation();
  const [swaps, setSwaps] = useState<Swap[]|null>(null);
  const [search, setSearch] = useState("");
  const [faceId, setFaceId] = useState<string|null>(null);

  useAsyncEffect(async () => {
    setSwaps(await getSwaps());
  }, []);

  if (swaps?.length == 0) {
    return <CenterContainer>
      <Trans>You have not yet generated swap videos.</Trans><br />
      <Link to='/create'><LetsCreate><Trans>Let's create swap!</Trans></LetsCreate></Link>
    </CenterContainer>;
  }

  // 絞り込みに出す顔は、実際に動画を作ったものだけ。同じ写真を上げ直すと
  // `Face` は増えるので `hash` で 1 つにまとめる。
  const usedFaces = swaps
    ? removeDuplicates(swaps.map(swap => swap.face).filter(face => face), face => face.hash ?? face.faceId)
    : [];
  const selectedFace = usedFaces.find(face => face.faceId === faceId);
  const matchesFace = (swap: Swap) => {
    if (!selectedFace) {
      return true;
    }
    if (selectedFace.hash) {
      return swap.face?.hash === selectedFace.hash;
    }
    return swap.faceId === selectedFace.faceId;
  };
  const shown = swaps?.filter(swap => matchesFace(swap) && matchesSwapQuery(swap, search));

  return <PageContainer>
    <PageHeader>
      <PageTitle><Trans>Videos</Trans></PageTitle>
      {shown && <SwapCount>{shown.length}</SwapCount>}
    </PageHeader>
    {swaps && <Filters>
      <SearchInput
        value={search}
        placeholder={t("Search by video title")}
        onChange={event => setSearch(event.target.value)} />
      <FaceFilter faces={usedFaces} faceId={faceId} onChange={setFaceId} />
    </Filters>}
    <SwapListContainer>
      {shown
        ? shown.map(swap => <SwapItem key={swap.swapId} swap={swap} />)
        : Array.from({ length: SKELETON_COUNT }, (_, index) => <SwapItemSkeleton key={index} />)}
    </SwapListContainer>
    {shown && shown.length == 0 && <NoResult>
      <Trans>No videos matched your search</Trans>
    </NoResult>}
  </PageContainer>;
};

export default SwapListPage;
